import { AlertCircle, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAssessmentFixtureRun } from "@/hooks/admin/assessmentFixtures";
import { AdminLayout } from "@/layouts/AdminLayout";

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface ReviewSection {
  name: string;
  score: number;
  max_score: number;
  command_word_level: string;
  detected_behaviours: string[];
  missing_behaviours: string[];
  layers: Array<{
    name: string;
    score: number;
    max_points: number;
    components: Array<{
      code: string;
      label: string;
      score: number;
      max_points: number;
    }>;
  }>;
  cap_applied: boolean;
  cap_reason: string | null;
  short_rationale: string;
  next_step_to_improve: string;
  errored?: boolean;
  error_message?: string | null;
}

interface ReviewResult {
  is_relevant: boolean;
  relevance_score: number;
  relevance_explanation: string;
  sections: ReviewSection[];
  final_score: number;
}

function ResultView({ result }: { result: ReviewResult }) {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border p-5">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-gray-900">Final score</h2>
          <span className="text-3xl font-bold text-amber-600">{result.final_score}</span>
        </div>
        <div className="text-sm text-gray-600">
          Relevance: <strong>{result.relevance_score}/100</strong>
          {!result.is_relevant && (
            <span className="ml-2 px-2 py-0.5 rounded bg-red-100 text-red-800 text-xs">
              not relevant
            </span>
          )}
        </div>
        {result.relevance_explanation && (
          <p className="text-sm text-gray-700 mt-2">{result.relevance_explanation}</p>
        )}
      </div>

      {result.sections.map((section) => (
        <div
          key={section.name}
          className={`rounded-lg border p-5 ${section.errored ? "bg-amber-50 border-amber-300" : "bg-white"}`}
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                {section.name}
                {section.errored && (
                  <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded bg-amber-200 text-amber-900 align-middle">
                    errored
                  </span>
                )}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">Level: {section.command_word_level}</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">
                {section.score}
                <span className="text-sm font-normal text-gray-400">/{section.max_score}</span>
              </div>
              {section.cap_applied && section.cap_reason && (
                <p className="text-xs text-amber-700 mt-1">⚠ {section.cap_reason}</p>
              )}
            </div>
          </div>

          {section.errored && section.error_message && (
            <div className="mb-3 p-3 rounded bg-amber-100 border border-amber-200">
              <p className="text-xs font-semibold text-amber-900 mb-1">Assessment failure</p>
              <p className="text-xs text-amber-800 font-mono break-all">{section.error_message}</p>
            </div>
          )}

          {section.short_rationale && (
            <p className="text-sm text-gray-700 mb-3">{section.short_rationale}</p>
          )}

          {section.layers.length > 0 && (
            <div className="mt-3 space-y-2">
              {section.layers.map((layer) => (
                <div key={layer.name} className="border-l-2 border-gray-200 pl-3">
                  <div className="text-sm font-medium text-gray-700">
                    {layer.name}{" "}
                    <span className="text-xs text-gray-400">
                      ({layer.score}/{layer.max_points})
                    </span>
                  </div>
                  <ul className="text-xs text-gray-600 mt-1 space-y-0.5">
                    {layer.components.map((c) => (
                      <li key={c.code}>
                        <code className="text-gray-500">{c.code}</code> {c.label}{" "}
                        <span className="text-gray-400">
                          {c.score}/{c.max_points}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {section.detected_behaviours.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-semibold text-gray-700 mb-1">Detected behaviours</p>
              <ul className="text-xs text-gray-600 list-disc pl-4 space-y-0.5">
                {section.detected_behaviours.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            </div>
          )}

          {section.missing_behaviours.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-semibold text-gray-700 mb-1">Missing behaviours</p>
              <ul className="text-xs text-gray-600 list-disc pl-4 space-y-0.5">
                {section.missing_behaviours.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            </div>
          )}

          {section.next_step_to_improve && (
            <div className="mt-3 text-xs text-gray-600">
              <span className="font-semibold text-gray-700">Next step: </span>
              {section.next_step_to_improve}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AdminAssessmentRunDetailPage() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const runQuery = useAssessmentFixtureRun(runId, { poll: true });
  const run = runQuery.data ?? null;
  const loading = runQuery.isPending;
  const error = runQuery.isError
    ? runQuery.error instanceof Error
      ? runQuery.error.message
      : "Failed to load run"
    : null;

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-5xl">
        <button
          type="button"
          onClick={() =>
            run
              ? navigate(`/admin/assessment-fixtures/${run.fixture_id}`)
              : navigate("/admin/assessment-fixtures")
          }
          className="inline-flex items-center gap-1 text-sm text-white/70 hover:text-white mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Back to fixture
        </button>

        <h1 className="text-2xl font-semibold mb-1 text-white">Assessment Run</h1>
        {run && (
          <p className="text-sm text-white/60 mb-6">
            Ran {formatDate(run.ran_at)}
            {run.prompt_version && (
              <span className="ml-2">
                · prompt <code className="text-xs">{run.prompt_version}</code>
              </span>
            )}
          </p>
        )}

        {run?.status === "pending" && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
            <div>
              <p className="font-medium text-amber-900">Running…</p>
              <p className="text-sm text-amber-800">
                The marker is processing. This page updates automatically.
              </p>
            </div>
          </div>
        )}

        {run?.status === "failed" && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <p className="font-medium text-red-900">Run failed</p>
              <p className="text-sm text-red-800 mt-1">{run.error_message || "Unknown error"}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {run?.status === "completed" && run.result_json && (
          <>
            <div className="mb-4 inline-flex items-center gap-2 text-green-700 text-sm font-medium">
              <CheckCircle2 className="w-4 h-4" /> Completed {formatDate(run.completed_at)}
            </div>
            <ResultView result={run.result_json as unknown as ReviewResult} />
          </>
        )}
      </div>
    </AdminLayout>
  );
}
